// ================================================================
// UI-Command-Bus — Voice/Assistent steuert die SICHTBARE App.
// ================================================================
// Backend-Tools liefern `ui_command`-Signale (open_lead, open_view, …);
// die AssistantBar dispatcht sie hier, und jede Shell (Mobile Lead-UI,
// Company Dashboard) registriert Handler auf ihre EIGENEN Navigations-
// Primitive (setTab/setSelected bzw. setView). So fühlt sich Voice an
// wie ein Klick — ohne dass der Assistent die Shells kennen muss.
//
// Eigenes kleines Registry statt core/events.js, weil dispatch hier eine
// Antwort braucht: `true` = eine Shell hat den Befehl ausgeführt. Damit
// kann der Assistent dem Nutzer ehrlich sagen, wenn ein Screen in der
// aktuellen Ansicht nicht verfügbar ist.

export type UiCommand =
  | { type: 'OPEN_LEAD'; leadId: string }
  // docType: Desktop öffnet das OfferForm im passenden Modus (Rechnung aus
  // convert_offer_to_invoice); fehlt es, gilt OFFER. Mobil ist das
  // Dokument-Sheet ohnehin typbewusst und ignoriert das Feld.
  | { type: 'OPEN_OFFER'; documentId: string; leadId?: string | null; docType?: 'OFFER' | 'INVOICE' }
  // «+ Neue Offerte» per Stimme: öffnet das Neue-Offerte-Sheet der App (mobil)
  // bzw. den Kunden-Picker (Desktop) — wie ein Fingertipp auf «+». prefill
  // füllt das «Neuer Kunde»-Formular des Sheets mit diktierten Angaben vor.
  // positions: bereits diktierte Arbeiten («Teppich waschen, Fenster putzen»).
  // Der Entwurf entsteht erst hier auf dem Client — nach Kundenwahl/-anlage —,
  // darum reisen sie mit und werden sofort danach eingetragen. Ohne das wäre
  // das Diktat nach dem Kunden-Umweg vergessen.
  | { type: 'OPEN_NEW_OFFER'; prefill?: { name?: string; phone?: string; street?: string; plz?: string; city?: string; email?: string }; positions?: Array<{ title: string; quantity: number; unit: string | null; unitPrice: number }> }
  // Voice tippt SICHTBAR ins «Neuer Kunde»-Formular des offenen Neue-Offerte-
  // Sheets (Schwester von POSITION_FORM): fields erscheinen einzeln während
  // des Diktats, create drückt «Kunde anlegen & Offerte starten».
  | { type: 'NEW_OFFER_FORM'; fields?: { name?: string; phone?: string; street?: string; plz?: string; city?: string; email?: string }; clear?: string[]; create?: boolean }
  // Der schwebende Mikrofon-Orb ist im Weg → an den genannten Rand schieben
  // («ببر بالا», «nach rechts»). Handler: OrbFab (Mobile).
  | { type: 'MOVE_MIC'; vertical?: 'top' | 'bottom'; horizontal?: 'left' | 'right' }
  // Voice bedient den Send-Dialog des offenen Dokument-Sheets: öffnen,
  // Kanal wählen, Empfänger überschreiben, send drückt «Jetzt senden».
  // Handler: MobileOfferSheet (nur wenn documentId zum offenen Sheet passt).
  | { type: 'SEND_SHEET'; documentId?: string; channel?: 'email' | 'whatsapp'; toEmail?: string; phone?: string; send?: boolean }
  // «Keine WhatsApp-Nummer verbunden» → WhatsApp-Bereich öffnen und dort den
  // «Nummer verbinden»-Dialog (Kopplungscode) aufklappen, Nummer vorbefüllt.
  | { type: 'WHATSAPP_CONNECT'; phone?: string; country?: string }
  // filter = Unter-Tab der Liste («die ANGENOMMENEN Offerten»). Ohne ihn
  // bleibt der zuletzt gewählte Filter stehen — wer nur «zeig die Offerten»
  // sagt, will nicht, dass sich sein Filter heimlich ändert.
  // date = Kalender-Fokus (YYYY-MM-DD): der Kalender öffnet auf DIESEM Tag —
  // Voice-Termin-Tools schicken den Tag mit, damit der Nutzer die Änderung sieht.
  | { type: 'OPEN_VIEW'; view: string; filter?: 'open' | 'accepted' | 'sent' | 'all' | 'unpaid' | 'paid' | 'overdue' | 'awaiting'; date?: string }
  // Voice «öffne die Mail von X»: der E-Mail-Bereich öffnet DIESE Mail in der
  // Detail-Ansicht (open_email-Tool). Handler: LeadMobileView (mobil, per
  // Request-Prop an MobileEmail) bzw. CompanyDashboard (Desktop, emailView).
  | { type: 'OPEN_EMAIL'; emailId: string }
  // Generisches Antippen eines markierten Elements (data-voice-target/data-nav)
  // mit sichtbarem Geister-Zeiger — Handler: core/voiceTargets.js.
  | { type: 'TAP_TARGET'; name: string }
  | { type: 'SCROLL'; direction: 'down' | 'up' | 'top' | 'bottom' }
  // Voice steuert das sichtbare «+ Position»-Formular des offenen Dokuments:
  // fields erscheinen einzeln waehrend des Diktats, save loest das Speichern aus.
  | { type: 'POSITION_FORM'; documentId: string; save?: boolean; fields?: { title?: string; quantity?: number; unit?: string; unitPrice?: number } }
  // Ein Voice-Tool hat dieses Dokument serverseitig geändert (Position
  // diktiert etc.) — offene Ansichten ziehen live nach.
  | { type: 'DOCUMENT_CHANGED'; documentId: string }
  // Ein Voice-Tool hat Termine geändert (add/delete_appointment) —
  // der offene Kalender lädt live nach.
  | { type: 'APPOINTMENTS_CHANGED' }
  // «Erst zeigen, dann senden»: der Assistent hat eine Kundennachricht
  // FERTIG, aber NICHT gesendet. Die Karte zeigt Empfänger, Betreff und den
  // vollen Text mit zwei Knöpfen — abgeschickt wird erst hier.
  // Handler: OutboundConfirmCard in der AssistantBar (shell-agnostisch, sie
  // ist auf beiden Oberflächen gemountet).
  | {
      type: 'CONFIRM_OUTBOUND';
      outboundId: string;
      channel: 'EMAIL';
      kind: 'FREE' | 'REPLY' | 'DOCUMENT';
      recipient: string;
      recipientName?: string | null;
      subject?: string | null;
      body?: string | null;
      documentLabel?: string | null;
    }
  // Der Entwurf ist erledigt — mündlich bestätigt (confirm_outbound) oder
  // verworfen (cancel_outbound). Die Karte muss dann vom Bildschirm: sonst
  // sagt der Assistent «ist gesendet» und die Bestätigungskarte hängt noch
  // davor. Kein Navigations-Befehl: bleibt er ohne Handler, ist nichts falsch.
  | { type: 'OUTBOUND_RESOLVED'; outboundId?: string | null; state: 'sent' | 'cancelled' };

type CommandOf<T extends UiCommand['type']> = Extract<UiCommand, { type: T }>;

const handlers = new Map<string, Set<(cmd: UiCommand) => boolean>>();

/** Handler für EINEN Befehlstyp registrieren. Rückgabe = unsubscribe. */
export function onUiCommand<T extends UiCommand['type']>(
  type: T,
  handler: (cmd: CommandOf<T>) => boolean,
): () => void {
  let set = handlers.get(type);
  if (!set) {
    set = new Set();
    handlers.set(type, set);
  }
  const h = handler as (cmd: UiCommand) => boolean;
  set.add(h);
  return () => { handlers.get(type)?.delete(h); };
}

// ── UI-Kontext: welches Dokument liegt gerade sichtbar offen? ──
// Die Formulare (MobileOfferSheet, OfferForm im Edit-Modus) melden sich an/ab;
// die AssistantBar schickt den Wert bei jedem Tool-Call mit (ui_context), damit
// Diktat-Tools das OFFENE Dokument treffen statt «neuester Entwurf».
let openDocumentId: string | null = null;
export function setOpenDocument(id: string | null): void { openDocumentId = id; }
export function getOpenDocument(): string | null { return openDocumentId; }

// ── Welches Dokument liegt im SENDE-Dialog, und seit welcher Runde? ──
// Das Sende-Sheet meldet sich hier an und ab; die AssistantBar schickt den
// Wert als ui_context.send_sheet mit. Der Server lässt `send=true` nur zu,
// wenn der Dialog schon in einer FRÜHEREN Gesprächsrunde offen war — sonst
// hat der Nutzer den Empfänger nie gesehen (open-send-sheet.tool.ts).
let sendSheet: { documentId: string; turnKey: string | null } | null = null;
export function setSendSheetOpen(documentId: string | null, turnKey: string | null): void {
  sendSheet = documentId ? { documentId, turnKey } : null;
}
export function getSendSheetOpen(): { documentId: string; turnKey: string | null } | null { return sendSheet; }

/** Befehl ausführen. `true`, sobald EIN Handler ihn übernommen hat. */
export function dispatchUiCommand(cmd: UiCommand): boolean {
  const set = handlers.get(cmd.type);
  if (!set || !set.size) return false;
  let handled = false;
  set.forEach((h) => {
    try {
      if (h(cmd)) handled = true;
    } catch (err) {
      console.error(`[uiCommands] handler failed for "${cmd.type}":`, err);
    }
  });
  return handled;
}
